import { DroppedImage } from "@lib/store/src/mark";
import { ResourcesList } from "generated";
import { createConnectionLine } from "./connection-line";
import { bindImageToElement, getStoreByControlId, showTemporaryTooltip, updateImageTooltip, updateLastModify  } from "./image-tools";
import { runInAction } from "mobx";
import i18next from "i18next";

export function handleImageDrop(
  e: DragEvent,
  markArray: any[],
  control: any,
): DroppedImage | null {
  const dataStr = e.dataTransfer?.getData("text/plain");
  if (!dataStr) return null;
  
  let parsed;
  try {
    parsed = JSON.parse(dataStr);
  } catch (err) {
    console.warn(`${i18next.t("mark_username")} drop:`, dataStr, err);
    return null;
  }

  const { resourcename, offsetX = 0, offsetY = 0, controlId = null } = parsed;
  if (!resourcename) return null;

  const resourcePath = ResourcesList.resourcesList[resourcename];
  if (!resourcePath) return null;

  const matchedMark = markArray.find((m) => m.resourcename === resourcename);
  const win = document.elementFromPoint(e.clientX, e.clientY)?.closest("ms-window");
  const windowId = win?.getAttribute("data-window-id") ?? win?.id ?? null;

  let x = e.clientX - offsetX;
  let y = e.clientY - offsetY;

  if (win) {
      const winRect = win.getBoundingClientRect();
      const scale = winRect.width / win.offsetWidth;

      x = (e.clientX - winRect.left - offsetX) / scale;
      y = (e.clientY - winRect.top  - offsetY) / scale;
  }
  const imgData: DroppedImage = {
    id: crypto.randomUUID(),
    src: `/resources/${resourcePath}`,
    x: x,
    y: y,
    propertysubtypes: matchedMark?.propertysubtypes,
    setvalue: matchedMark?.setvalue,
    priority: matchedMark?.priority ?? 0, 
    width: 200,
    height: null,
    timestamp: Date.now(),
    origin: parsed.origin ?? "mark",
    controlId,
    windowId
  };
  const user = $sw.serverState.UserData.user || "";
  imgData.commentAuthor = user;
  imgData.commentDate = new Date().toISOString();
  const store = getStoreByControlId(controlId)
  runInAction(() => {
    store.droppedImages.push(imgData)
  })
  return imgData;
}

function getVisibleConnectionLine(): SVGLineElement | null {
  return document.querySelector("svg.connection-line line");
}

function isPointNearLine(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  tolerance: number
) {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) {
    param = dot / lenSq;
  }

  let xx, yy;
  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = px - xx;
  const dy = py - yy;
  return Math.sqrt(dx * dx + dy * dy) <= tolerance;
}

export function showConnectionLine(img: HTMLImageElement, imgData: DroppedImage) {

  removeConnectionLineForce(img);
  const store = getStoreByControlId(imgData.controlId);
  const realImg = store?.droppedImages.find(i => i.id === imgData.id)
  if(!realImg) return;
  if(!realImg.linkedElementId) return;
  const startSide = imgData.linkSide ?? "right";
  const endSide = imgData.targetSide ?? "left";
  
  removeConnectionLine(img);
  const targetId = realImg.linkedElementId;
  if (!targetId) return;
  const target = findElementInSameWindow(img, targetId);
  if (!target) return;

    const imgRect = img.getBoundingClientRect();

    let startX, startY;

    switch (startSide) {
      case "left":
        startX = imgRect.left;
        startY = imgRect.top + imgRect.height / 2;
        break;
      case "right":
        startX = imgRect.right;
        startY = imgRect.top + imgRect.height / 2;
        break;
      case "top":
        startX = imgRect.left + imgRect.width / 2;
        startY = imgRect.top;
        break;
      case "bottom":
        startX = imgRect.left + imgRect.width / 2;
        startY = imgRect.bottom;
        break;
      default:
        startX = imgRect.left + imgRect.width / 2;
        startY = imgRect.top + imgRect.height / 2;
    }

    const endRect = target.getBoundingClientRect();
    let endX, endY;

    switch (endSide) {
      case "left":
        endX = endRect.left;
        endY = endRect.top + endRect.height / 2;
        break;

      case "right":
        endX = endRect.right;
        endY = endRect.top + endRect.height / 2;
        break;

      case "top":
        endX = endRect.left + endRect.width / 2;
        endY = endRect.top;
        break;

      case "bottom":
        endX = endRect.left + endRect.width / 2;
        endY = endRect.bottom;
        break;

      default:
        endX = endRect.left + endRect.width / 2;
        endY = endRect.top  + endRect.height / 2;
    }

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.classList.add("connection-line");
    Object.assign(svg.style, {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: "99999999999",
    });

    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("stroke", store.connectionlinecolor);
    line.setAttribute("stroke-width", "2");
    line.setAttribute("x1", `${startX}`);
    line.setAttribute("y1", `${startY}`);
    line.setAttribute("x2", `${endX}`);
    line.setAttribute("y2", `${endY}`);
    line.setAttribute("data-img-id", imgData.id)

    svg.appendChild(line);
    document.body.appendChild(svg);

    (img as any)._activeSvg = svg;
}

export function findElementInSameWindow(img: HTMLImageElement, elementId: string): HTMLElement | null {
  const win = img.closest("ms-window") as HTMLElement | null;
  if (!win) return document.getElementById(elementId) as HTMLElement | null;
  return win.querySelector<HTMLElement>(`#${CSS.escape(elementId)}`);
}


export function removeConnectionLine(img: HTMLImageElement) {
  if((img as any)._forceShowConnection) return;
  const svg = (img as any)._activeSvg;
  if (svg) {
    svg.remove();
    delete (img as any)._activeSvg;
  }
}

export function removeConnectionLineForce(img: HTMLImageElement) {
  const svg = (img as any)._activeSvg;
  if (svg) {
    svg.remove();
    delete (img as any)._activeSvg;
  }
}

export function detachImageConnection(imgData: DroppedImage) {
  const store = getStoreByControlId(imgData.controlId);
  if (!store) return;
  const realImg = store.droppedImages.find(i => i.id === imgData.id);
  if (!realImg) return
  if (realImg.linkedElementId && realImg.propertysubtypes) {
    if (realImg.linkedElementId && realImg.propertysubtypes) {
      const imgEl = Array.from(
        document.querySelectorAll<HTMLImageElement>(`img[data-id="${realImg.id}"]`)
      ).find(el => {
        const win = el.closest("ms-window") as HTMLElement | null;
        const winId = win?.getAttribute("data-window-id") ?? win?.id ?? null;
        return winId === realImg.windowId;
      });
      const el = imgEl ? (findElementInSameWindow(imgEl, realImg.linkedElementId) as any) : null;

      if (el) {
        const subtype = realImg.propertysubtypes.toLowerCase();
        el.attributeChangedCallback(subtype, el[subtype], 1, null, {forced: true});
        el[subtype] = null;
      }
    }
  }
  const oldLinkedId = realImg!.linkedElementId;
  runInAction(() => {
    realImg.linkedElementId = undefined;
    realImg.linkSide = undefined;
    imgData.linkedElementId = null;
    imgData.linkSide = null;
    imgData.targetSide = null;
    const user = $sw.serverState.UserData.user || "";
    updateLastModify(realImg, user)
    store.droppedImages = [...store.droppedImages];
    const imgEl = Array.from(document.querySelectorAll<HTMLImageElement>(`img[data-id="${realImg.id}"]`)).find(el => {
      const win = el.closest("ms-window");
      const winId =
        win?.getAttribute("data-window-id")
        ?? win?.id
        ?? null;
      return winId === realImg.windowId;
    });
    if(imgEl) updateImageTooltip(imgEl, realImg);
  });
  const imgEl = document.querySelector(`img[data-id="${imgData.id}"]`) as HTMLImageElement;
  if (imgEl) {
    removeConnectionLine(imgEl);
  }
  if (oldLinkedId) {
    const targetEl = document.getElementById(oldLinkedId);
    if (targetEl) unhighlightTarget(targetEl);
  }
}

let currentHoverTarget: HTMLElement | null = null;

function highlightTarget(el: HTMLElement) {
  el.style.outline = "2px solid #1e90ff";
  el.style.outlineOffset = "2px";
}

export function unhighlightTarget(el: HTMLElement | null) {
  if (!el) return;
  el.style.outline = "";
  el.style.outlineOffset = "";
}

export function isSideVisibleAtLeastHalf(el, side) {
  const rect = el.getBoundingClientRect();
  const samples = 20;
  let visible = 0;

  for (let i = 0; i < samples; i++) {
    let x, y;

    switch (side) {
      case "left":
        x = rect.left + 1;
        y = rect.top + (rect.height * i) / samples;
        break;

      case "right":
        x = rect.right - 1;
        y = rect.top + (rect.height * i) / samples;
        break;

      case "top":
        x = rect.left + (rect.width * i) / samples;
        y = rect.top + 1;
        break;

      case "bottom":
        x = rect.left + (rect.width * i) / samples;
        y = rect.bottom - 1;
        break;
    }

    const topEl = document.elementFromPoint(x, y);
    if (topEl === el || el.contains(topEl)) {
      visible++;
    }
  }

  const visibleRatio = visible / samples;
  return visibleRatio >= 0.5; 
}

function detectAttachSide(el: HTMLElement, x: number, y: number): "left"|"right"|"top"|"bottom" {
  const rect = el.getBoundingClientRect();
  const offsetLeft   = Math.abs(x - rect.left);
  const offsetRight  = Math.abs(x - rect.right);
  const offsetTop    = Math.abs(y - rect.top);
  const offsetBottom = Math.abs(y - rect.bottom);

  const minOffset = Math.min(offsetLeft, offsetRight, offsetTop, offsetBottom);

  switch (minOffset) {
    case offsetLeft:   return "left";
    case offsetRight:  return "right";
    case offsetTop:    return "top";
    case offsetBottom: return "bottom";
  }
}

export function startDragConnection(
  e: MouseEvent,
  anchor: HTMLElement,
  imgData: DroppedImage,
) {
  e.stopPropagation();
  e.preventDefault();
  const store = getStoreByControlId(imgData.controlId);
  const outlineColor = store?.selectionoutlinecolor;

  const imgEl = anchor.closest(".image-container")?.querySelector("img.dropped-image") as HTMLImageElement | null;
  if (imgEl) imgEl.style.outline = `4px solid ${outlineColor}`;

  const win = anchor.closest("ms-window") as HTMLElement | null;
  if(!win) return;
  const winRect = win.getBoundingClientRect();
  const scale = winRect.width / win.offsetWidth || 1;
  const line = createConnectionLine(win , imgData.controlId!);
  line.setAttribute("data-img-id", imgData.id)

  const rect = anchor.getBoundingClientRect();
  const startX = rect.left + rect.width / 2;
  const startY = rect.top + rect.height / 2;
  let localStartX = (startX - winRect.left) / scale;
  let localStartY = (startY - winRect.top) / scale;

  const side = anchor.dataset.side;
  if (side) imgData.linkSide = side;

  line.setAttribute("x1", `${localStartX}`);
  line.setAttribute("y1", `${localStartY}`);
  line.setAttribute("x2", `${localStartX}`);
  line.setAttribute("y2", `${localStartY}`);

  const onMouseMove = (moveEvent: MouseEvent) => {
    let x = moveEvent.clientX;
    let y = moveEvent.clientY;
    x = (x - winRect.left) / scale;
    y = (y - winRect.top)  / scale;

    const cursorX = moveEvent.clientX;
    const cursorY = moveEvent.clientY;

    if (
      cursorX < winRect.left ||
      cursorX > winRect.right ||
      cursorY < winRect.top ||
      cursorY > winRect.bottom
    ) {
      const svgCancel = (line as any)._svg;
      if (svgCancel) svgCancel.remove();
      unhighlightTarget(currentHoverTarget);
      currentHoverTarget = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      return;
    }

    line.setAttribute("x2", `${x}`);
    line.setAttribute("y2", `${y}`);

    const el = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY) as HTMLElement;
    let candidate: HTMLElement | null = null;
    if (el && el.id && !el.closest(".dropped-image") && !(el instanceof SVGElement)) {
      const targetWin = el.closest("ms-window") as HTMLElement | null;
      if (!win || !targetWin || win === targetWin) {
        candidate = el;
      }
    }

    if (candidate !== currentHoverTarget) {
      unhighlightTarget(currentHoverTarget);
      if (candidate) highlightTarget(candidate);
      currentHoverTarget = candidate;
    }
  };

  const onMouseUp = (upEvent: MouseEvent) => {
    const releaseX = upEvent.clientX;
    const releaseY = upEvent.clientY;

    const isInsideWindow = releaseX >= winRect.left && releaseX <= winRect.right && releaseY >= winRect.top && releaseY <= winRect.bottom;

    if (!isInsideWindow) {
      const svgCancel = (line as any)._svg;
      if (svgCancel) svgCancel.remove();
      unhighlightTarget(currentHoverTarget);
      currentHoverTarget = null;
      return;
    }
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    if (imgEl) {
      imgEl.style.outline = "";
    }

    const svg = (line as any)._svg;
    svg.remove();
    unhighlightTarget(currentHoverTarget);
    currentHoverTarget = null;

    const target = document.elementFromPoint(upEvent.clientX, upEvent.clientY);
    if (
      target &&
      !target.closest(".dropped-image") &&
      !(target instanceof SVGElement)
    ) {
      const el = target as HTMLElement;

      if (el.id) {
        const targetWin = el.closest("ms-window")
        if(win && targetWin && win !== targetWin) return;
        const attachSide = detectAttachSide(el, upEvent.clientX, upEvent.clientY);
        if(!isSideVisibleAtLeastHalf(el, attachSide)) {
          showTemporaryTooltip(el , `${i18next.t("unavailable_side")}`)
          return;
        }
        imgData.targetSide = attachSide;
        bindImageToElement(imgData, el);
      }
    }
  };
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
}

function showLineContextMenu(
  x: number,
  y: number,
  imgData: DroppedImage
) {
  removeLineContextMenu();

  const menu = document.createElement("div");
  menu.className = "line-context-menu";
  menu.style.position = "fixed";
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
  menu.style.zIndex = "999999999999";

  const item = document.createElement("div");
  item.className = "line-context-menu-item";
  item.textContent = i18next.t("break_connection");

  item.addEventListener("click", () => {
    removeLineContextMenu();
    detachImageConnection(imgData)
  });

  menu.appendChild(item);
  document.body.appendChild(menu);
}

function removeLineContextMenu() {
  document.querySelector(".line-context-menu")?.remove();
}

export function setupClickOutsideHandler() {
  document.addEventListener(
    "mousedown",
    (e) => {
      if ((e as MouseEvent).button === 2) {
        const line = getVisibleConnectionLine();
        if (line) {
          const x1 = parseFloat(line.getAttribute("x1") || "0");
          const y1 = parseFloat(line.getAttribute("y1") || "0");
          const x2 = parseFloat(line.getAttribute("x2") || "0");
          const y2 = parseFloat(line.getAttribute("y2") || "0");

          const svg = line.ownerSVGElement!;
          const rect = svg.getBoundingClientRect();

          const px = e.clientX - rect.left;
          const py = e.clientY - rect.top;

          const hit = isPointNearLine(px, py, x1, y1, x2, y2, 6);

          if (hit) {
            e.preventDefault();
            e.stopPropagation();
            (e as any).stopImmediatePropagation?.();
            const imgId = line.getAttribute("data-img-id");
            if (!imgId) return;

            const store = getStoreByControlId(
              (document.querySelector(`img[data-id="${imgId}"]`) as any)?.dataset?.controlId
            );
            if (!store) return;

            const imgData = store.droppedImages.find(i => i.id === imgId);
            if (!imgData) return;
            showLineContextMenu(e.clientX, e.clientY, imgData);
            return;
          }
        }
      }
      const isImage = (e.target as HTMLElement)?.closest(".dropped-image");
      if (isImage) return;

      if (!(e.target as HTMLElement)?.closest(".line-context-menu")) {
        removeLineContextMenu();
      }

      document.querySelectorAll<HTMLElement>(".dropped-image.selected").forEach(el => {
        el.classList.remove("selected");
        el.style.border = "";
        el.parentElement?.querySelector(".anchor-point")?.remove();
      });
      document.querySelectorAll("svg.connection-line").forEach(line => line.remove());
    },
    true
  );
}
